import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getCurrentPosition, watchPosition, clearWatch } from '@/lib/geo';
import { useT } from '@/lib/i18n';
import { useApp } from '@/lib/store';
import { HoldButton } from '@/components/HoldButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ChevronLeft, MapPin, MessageCircle, Phone, Volume2, Sun, Flashlight, EyeOff, X, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { smsHref } from '@/lib/format';

export default function SafetyMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const sosRideId = useApp((s) => s.sosRideId);
  const setSosRideId = useApp((s) => s.setSosRideId);
  const locked = sosRideId === id; // SOS engaged for this ride → keep app open
  const [numbers, setNumbers] = useState([]);
  const [alarmOn, setAlarmOn] = useState(false);
  const [sosScreen, setSosScreen] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const audioCtx = useRef(null);
  const oscRef = useRef(null);
  const trackRef = useRef(null);
  const wakeRef = useRef(null);

  // --- SOS LOCK: keep the app open & visible until the rider ends SOS ---
  useEffect(() => {
    if (!locked) return;

    // 1) Screen Wake Lock — stop the screen from dimming/sleeping.
    let released = false;
    const acquireWake = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        // Non-fatal: some browsers block wake lock (e.g. low battery). SOS still works.
        console.warn('SOS wake lock unavailable:', err);
      }
    };
    acquireWake();
    // Re-acquire if the OS drops it when tab is backgrounded then refocused.
    const onVisible = () => { if (document.visibilityState === 'visible' && !released) acquireWake(); };
    document.addEventListener('visibilitychange', onVisible);

    // 2) Warn before closing/refreshing the tab.
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', onBeforeUnload);

    // 3) Trap in-app / browser back navigation — re-pin to this screen.
    window.history.pushState({ sosLock: true }, '');
    const onPop = () => {
      window.history.pushState({ sosLock: true }, '');
      toast.info(t('sos_locked_note'));
    };
    window.addEventListener('popstate', onPop);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPop);
      try { wakeRef.current && wakeRef.current.release(); } catch (e) { console.debug('wake lock release failed (ignorable):', e); }
      wakeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, id]);

  const endSos = () => {
    stopAlarm(); stopTorch();
    setSosRideId(null);
    try { wakeRef.current && wakeRef.current.release(); } catch (e) { console.debug('wake lock release failed (ignorable):', e); }
    wakeRef.current = null;
    api.addSafetyEvent(id, { event_type: 'sos_ended' }).catch(() => {});
    toast.success(t('sos_ended'));
    navigate('/app');
  };

  useEffect(() => {
    api.emergencyNumbers('PH').then((d) => setNumbers(d.numbers)).catch(() => {});
    api.addSafetyEvent(id, { event_type: 'safety_mode_opened' }).catch(() => {});
    return () => { stopAlarm(); stopTorch(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep posting live location while in Safety Mode so the trusted contact can
  // track the rider during an emergency (ActiveRide is unmounted here).
  useEffect(() => {
    let wId = null;
    let lastPost = 0;
    getCurrentPosition().then((p) => { if (navigator.onLine) api.postLocation(id, p).catch(() => {}); }).catch(() => {});
    wId = watchPosition((p) => {
      if (navigator.onLine && Date.now() - lastPost > 8000) { lastPost = Date.now(); api.postLocation(id, p).catch(() => {}); }
    }, () => {});
    return () => clearWatch(wId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const shareLocation = async () => {
    setBusy(true);
    try { const p = await getCurrentPosition(); await api.postLocation(id, p); await api.addSafetyEvent(id, { event_type: 'share_started', latitude: p.latitude, longitude: p.longitude }); toast.success(t('sm_shared')); }
    catch { toast.error(t('sm_share_err')); }
    finally { setBusy(false); }
  };

  const alertContact = async () => {
    if (!navigator.onLine) { toast.error(t('sm_offline_alert')); return; }
    setBusy(true);
    try {
      let lat, lng;
      try { const p = await getCurrentPosition(); lat = p.latitude; lng = p.longitude; await api.postLocation(id, p); } catch (e) { console.debug('SOS location fetch/post failed (continuing):', e); }
      const res = await api.addSafetyEvent(id, { event_type: 'alert_contact', latitude: lat, longitude: lng });
      if (res?.email_status === 'success') toast.success(t('sm_alerted'));
      else if (res?.email_status === 'skipped_daily_cap') toast.warning(t('sm_alert_email_fail'));
      else if (res?.email_status === 'error') toast.warning(t('sm_alert_email_fail'));
      else toast.info(t('sm_alert_no_email'));
    }
    catch (e) {
      const code = e?.response?.status;
      toast.error(code === 429 ? t('sm_alert_rate') : t('sm_offline_alert'));
    }
    finally { setBusy(false); }
  };

  const startAlarm = async () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtx.current = ctx;
      // Contexts can start suspended; a user gesture (this tap) lets us resume.
      try { await ctx.resume(); } catch (e) { console.debug('audio resume failed (ignorable):', e); }
      // Best-effort: route to the device's DEFAULT output (loudspeaker on desktop).
      // NOTE: browsers/mobile OSes do NOT allow overriding the active headphone
      // route or the system volume from a web app — this is a platform limit.
      try { if (typeof ctx.setSinkId === 'function') await ctx.setSinkId(''); }
      catch (e) { console.debug('setSinkId unsupported (ignorable):', e); }

      // Limiter → lets us drive the signal very loud without harsh clipping.
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6; limiter.knee.value = 0; limiter.ratio.value = 20;
      limiter.attack.value = 0.002; limiter.release.value = 0.1;

      // Master gain pushed near the ceiling (limiter tames the peaks).
      const master = ctx.createGain();
      master.gain.value = 0.95;

      // Two detuned oscillators (saw + square) = a richer, harsher, perceptually
      // LOUDER two-tone siren than a single tone at the same level.
      const oscA = ctx.createOscillator(); oscA.type = 'sawtooth';
      const oscB = ctx.createOscillator(); oscB.type = 'square';
      oscA.frequency.value = 900; oscB.frequency.value = 900; oscB.detune.value = 14;
      oscA.connect(master); oscB.connect(master);
      master.connect(limiter); limiter.connect(ctx.destination);
      oscA.start(); oscB.start();

      let hi = true;
      const iv = setInterval(() => {
        const f = hi ? 1200 : 700;
        oscA.frequency.setTargetAtTime(f, ctx.currentTime, 0.01);
        oscB.frequency.setTargetAtTime(f, ctx.currentTime, 0.01);
        hi = !hi;
      }, 350);

      // Strong continuous vibration as a physical alert (Android; iOS ignores).
      let vib = null;
      if ('vibrate' in navigator) {
        const pattern = [500, 150, 500, 150, 500, 300];
        try { navigator.vibrate(pattern); } catch (e) { console.debug('vibrate failed (ignorable):', e); }
        vib = setInterval(() => { try { navigator.vibrate(pattern); } catch (e) { console.debug('vibrate failed (ignorable):', e); } }, 2100);
      }

      oscRef.current = { oscA, oscB, master, limiter, iv, vib };
      setAlarmOn(true);
      api.addSafetyEvent(id, { event_type: 'loud_alarm' }).catch(() => {});
    } catch { toast.error(t('sm_alarm_err')); }
  };
  const stopAlarm = () => {
    if (oscRef.current) {
      clearInterval(oscRef.current.iv);
      clearInterval(oscRef.current.vib);
      try { navigator.vibrate && navigator.vibrate(0); } catch (e) { console.debug('vibrate cancel failed (ignorable):', e); }
      try { oscRef.current.oscA.stop(); } catch (e) { console.debug('oscA stop failed (ignorable):', e); }
      try { oscRef.current.oscB.stop(); } catch (e) { console.debug('oscB stop failed (ignorable):', e); }
    }
    if (audioCtx.current) { try { audioCtx.current.close(); } catch (e) { console.debug('audio context close failed (ignorable):', e); } }
    oscRef.current = null; audioCtx.current = null; setAlarmOn(false);
  };

  const startTorch = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const track = stream.getVideoTracks()[0];
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      if (caps.torch) { await track.applyConstraints({ advanced: [{ torch: true }] }); trackRef.current = track; setTorchOn(true); api.addSafetyEvent(id, { event_type: 'flashlight' }).catch(() => {}); }
      else { track.stop(); toast.info(t('sm_torch_fallback')); setSosScreen(true); }
    } catch { toast.info(t('sm_torch_fallback')); setSosScreen(true); }
  };
  const stopTorch = () => { if (trackRef.current) { try { trackRef.current.stop(); } catch (e) { console.debug('torch track stop failed (ignorable):', e); } } trackRef.current = null; setTorchOn(false); };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <div className="mx-auto max-w-md px-4 pt-4 pb-10">
        {locked ? (
          <div data-testid="sos-active-banner" className="rounded-2xl px-4 py-3 mb-4 flex items-center gap-3" style={{ background: 'var(--danger)', color: '#fff' }}>
            <ShieldAlert size={22} />
            <div>
              <div className="font-extrabold leading-tight">{t('sos_active_banner')}</div>
              <div className="text-xs" style={{ color: 'rgba(255,255,255,0.9)' }}>{t('sos_locked_note')}</div>
            </div>
          </div>
        ) : (
          <button onClick={() => navigate(`/app/ride/${id}`)} className="inline-flex items-center gap-1 text-sm font-semibold mb-3" style={{ color: 'var(--text-muted)' }}><ChevronLeft size={18} /> {t('sm_back')}</button>
        )}
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('sm_title')}</h1>
        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{t('sm_sub')}</p>

        <Tabs defaultValue="quiet">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="quiet" data-testid="safety-mode-quiet-tab"><EyeOff size={16} className="mr-1.5" /> {t('sm_quiet')}</TabsTrigger>
            <TabsTrigger value="loud" data-testid="safety-mode-loud-tab"><Volume2 size={16} className="mr-1.5" /> {t('sm_loud')}</TabsTrigger>
          </TabsList>

          <TabsContent value="quiet" className="space-y-2 mt-4">
            <Action onClick={shareLocation} disabled={busy} Icon={busy ? Loader2 : MapPin} spin={busy} title={t('sm_share')} text={t('sm_share_b')} />
            <Action onClick={alertContact} disabled={busy} Icon={MessageCircle} title={t('sm_alert')} text={t('sm_alert_b')} />
            <Action onClick={() => { window.location.href = smsHref('', `${t('sm_text_body')} ${window.location.origin}/app`); }} Icon={MessageCircle} title={t('sm_text')} text={t('sm_text_b')} />
            <EmergencyCalls numbers={numbers} title={t('sm_ecall')} note={t('sm_ecall_note')} onCall={() => api.addSafetyEvent(id, { event_type: 'emergency_call' }).catch(() => {})} />
          </TabsContent>

          <TabsContent value="loud" className="space-y-2 mt-4">
            <Action onClick={alarmOn ? stopAlarm : startAlarm} Icon={Volume2} danger active={alarmOn} title={alarmOn ? t('sm_alarm_stop') : t('sm_alarm')} text={t('sm_alarm_b')} />
            {alarmOn && (
              <p className="text-xs px-1 flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }} data-testid="alarm-volume-hint">
                <Volume2 size={13} /> {t('sm_alarm_hint')}
              </p>
            )}
            <Action onClick={() => setSosScreen(true)} Icon={Sun} danger title={t('sm_bright')} text={t('sm_bright_b')} />
            <Action onClick={torchOn ? stopTorch : startTorch} Icon={Flashlight} danger active={torchOn} title={torchOn ? t('sm_torch_off') : t('sm_torch')} text={t('sm_torch_b')} />
            <EmergencyCalls numbers={numbers} title={t('sm_ecall')} note={t('sm_ecall_note')} onCall={() => api.addSafetyEvent(id, { event_type: 'emergency_call' }).catch(() => {})} />
          </TabsContent>
        </Tabs>

        {locked && (
          <div className="mt-6">
            <HoldButton
              onComplete={endSos}
              Icon={ShieldCheck}
              idleLabel={t('sos_end_hold')}
              holdingLabel={t('sos_end_holding')}
              doneLabel={t('sos_ended')}
              testid="sos-end-hold-button"
            />
          </div>
        )}

        <p className="text-xs text-center mt-6" style={{ color: 'var(--text-muted)' }}>{t('disclaimer')}</p>
      </div>

      {sosScreen && <SosScreen onClose={() => setSosScreen(false)} help={t('sm_sos_help')} close={t('sm_close')} />}
    </div>
  );
}

const Action = ({ onClick, Icon, title, text, danger, active, disabled, spin }) => (
  <button onClick={onClick} disabled={disabled} className="w-full text-left rounded-2xl p-4 border flex items-center gap-3" style={{ background: active ? (danger ? 'var(--danger)' : 'var(--primary)') : 'var(--surface)', borderColor: danger ? 'var(--danger)' : 'var(--border)' }}>
    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl" style={{ background: active ? 'rgba(255,255,255,0.2)' : 'var(--surface-alt)', color: active ? '#fff' : (danger ? 'var(--danger)' : 'var(--primary)') }}>
      <Icon size={22} className={spin ? 'animate-spin' : ''} />
    </span>
    <div>
      <div className="font-bold" style={{ color: active ? '#fff' : 'var(--text)' }}>{title}</div>
      <div className="text-xs" style={{ color: active ? 'rgba(255,255,255,0.85)' : 'var(--text-muted)' }}>{text}</div>
    </div>
  </button>
);

const EmergencyCalls = ({ numbers, onCall, title, note }) => (
  <div className="rounded-2xl p-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
    <div className="flex items-center gap-2 mb-2"><Phone size={18} style={{ color: 'var(--danger)' }} /><span className="font-bold" style={{ color: 'var(--text)' }}>{title}</span></div>
    <div className="space-y-2">
      {numbers.map((n) => (
        <a key={n.number} href={`tel:${n.number}`} onClick={onCall} data-testid="safety-mode-emergency-call-button" className="flex items-center justify-between rounded-xl px-3 min-h-[48px] border" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }}>
          <span className="text-sm" style={{ color: 'var(--text)' }}>{n.label}</span>
          <span className="font-bold" style={{ color: 'var(--danger)' }}>{n.number}</span>
        </a>
      ))}
    </div>
    <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>{note}</p>
  </div>
);

const SosScreen = ({ onClose, help, close }) => {
  const [on, setOn] = useState(true);
  useEffect(() => { const iv = setInterval(() => setOn((v) => !v), 500); return () => clearInterval(iv); }, []);
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{ background: on ? '#DC2626' : '#FFFFFF' }}>
      <span className="text-6xl font-black" style={{ color: on ? '#FFFFFF' : '#DC2626' }}>SOS</span>
      <p className="mt-2 font-bold" style={{ color: on ? '#FFFFFF' : '#DC2626' }}>{help}</p>
      <button onClick={onClose} className="mt-8 inline-flex items-center gap-2 rounded-full px-5 min-h-[48px] font-bold" style={{ background: '#111', color: '#fff' }}><X size={18} /> {close}</button>
    </div>
  );
};
